import React from 'react';
import logoFull from '../../assets/scalesupport-logo.png';
import logoWordmark from '../../assets/scalesupport-wordmark.png';

/**
 * The real ScaleSupport logo.
 *
 * This used to be a hand-drawn SVG approximation of the brand mark -- bars, a
 * swoosh and the words set in the nearest available font. It was close, but it
 * was not the logo. It is now the artwork itself, with the white background
 * removed so it sits on any colour.
 *
 * Two files, because the tagline is part of the artwork and cannot be switched
 * off with a prop:
 *
 *   scalesupport-logo.png      the mark, the words and the tagline
 *   scalesupport-wordmark.png  the mark and the words, cropped above the tagline
 *
 * ON DARK BACKGROUNDS the logo sits on a white pill. The navy half of
 * "ScaleSupport" and the tagline are dark, so on the dark header they would be
 * nearly invisible -- the red reads and the rest disappears. The pill keeps the
 * brand colours exactly as drawn rather than recolouring somebody's logo.
 */

interface ScaleSupportLogoProps {
  /**
   * full    logo with tagline, for light backgrounds
   * compact wordmark only, for light backgrounds
   * white   for DARK backgrounds -- rendered on a white pill
   * badge   small, always on a white pill
   * icon    the graphic alone, for tight square spaces
   */
  variant?: 'full' | 'compact' | 'icon' | 'white' | 'badge';
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showTagline?: boolean;
}

export const ScaleSupportLogo: React.FC<ScaleSupportLogoProps> = ({
  variant = 'full',
  className = '',
  size = 'md',
  showTagline = true,
}) => {
  // Heights only. The width follows the artwork's own proportions, so the
  // logo can never be stretched by a container.
  const heightMap = {
    sm: { withTag: 'h-8', noTag: 'h-6' },
    md: { withTag: 'h-11', noTag: 'h-8' },
    lg: { withTag: 'h-14', noTag: 'h-10' },
    xl: { withTag: 'h-20', noTag: 'h-14' },
    /**
     * The sign-in mark at twice its old height (h-8 -> h-16), and the sidebar
     * mark about a third taller (h-14 -> 4.5rem). The sidebar figure is given
     * exactly rather than snapped to the nearest step, because the next step
     * up (h-20) overflows the menu bar it sits in.
     */
    '2xl': { withTag: 'h-[4.5rem]', noTag: 'h-16' },
  };

  const useTagline = showTagline && variant !== 'compact' && variant !== 'badge';
  const src = useTagline ? logoFull : logoWordmark;
  const heightClass = useTagline ? heightMap[size].withTag : heightMap[size].noTag;

  const img = (
    <img
      src={src}
      alt="ScaleSupport"
      className={`${heightClass} w-auto select-none shrink-0`}
      draggable={false}
    />
  );

  // The graphic alone. The artwork is one piece, so this crops to the mark on
  // the left rather than shipping a third file.
  if (variant === 'icon') {
    const iconSize = { sm: 'h-6', md: 'h-8', lg: 'h-10', xl: 'h-14', '2xl': 'h-16' }[size];
    return (
      <span
        className={`inline-block overflow-hidden ${iconSize} ${className}`}
        style={{ width: '1.35em', fontSize: 'inherit' }}
      >
        <img
          src={logoWordmark}
          alt="ScaleSupport"
          className={`${iconSize} w-auto max-w-none select-none`}
          draggable={false}
        />
      </span>
    );
  }

  // On a dark background: white pill, so the navy and the tagline stay legible.
  if (variant === 'white' || variant === 'badge') {
    return (
      <span
        className={`inline-flex items-center bg-white rounded-xl px-2.5 py-1 shadow-2xs ${className}`}
      >
        {img}
      </span>
    );
  }

  // Light backgrounds: the artwork on its own.
  return <span className={`inline-flex items-center ${className}`}>{img}</span>;
};

export default ScaleSupportLogo;
